import React, { ReactNode, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import iconBack from "../../assets/svg/icon-back.svg";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { JWT_KEY } from "../../config/constant";
import { postSurvey } from "../../apis/survey";
import { patchUserStatus } from "../../apis/auth";
import { getUserAgent } from "../../utils/utility";
import { getTopic, postNotification } from "../../apis/notification";
import { useModal } from "../../utils/useModal";
import { isTokenRefreshFailed } from "../../utils/errorCodes";

interface IAppHeader {
  back?: boolean;
  backFunc?: any;
  skip?: boolean;
  text?: string | ReactNode;
  narrow?: boolean;
  right?: string | ReactNode;
  rightFunc?: any;
}

const AppHeader = ({
  back,
  backFunc,
  skip,
  text,
  right,
  rightFunc,
}: IAppHeader) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation(["common", "auth", "errors"]);

  const [tailWidth, tailSetWidth] = useState<number>(0);

  const { openModal } = useModal();

  useEffect(() => {
    const element = document.querySelector(
      `${skip ? ".skip" : right ? ".right" : ".tail"}`
    );
    if (element) {
      const rect = element.getBoundingClientRect();
      const width = rect.width;
      tailSetWidth(width);
    }
  }, []);

  const skipFunc = async () => {
    const token = localStorage.getItem(JWT_KEY) as string;

    if (!token) return;

    try {
      const data = [{ surveyType: "SKIP", surveyAnswer: "SKIP" }];

      const { result, message } = await postSurvey(data, token);

      if (!result) return openModal({ type: "guide", message: message });
    } catch (e: any) {
      console.log(e);
      // TODO(i18n-phase4): BE 가 errorCode 로 응답하면 그걸 비교. 지금은 message 문자열 비교 유지.
      if (isTokenRefreshFailed(e))
        openModal({
          type: "login",
          message: t("auth:login.autoLogoutWarning"),
        });
      else openModal({ type: "guide", message: t("errors:generic.shortTitle") });
    }

    try {
      const { isSuccess, result, message } = await patchUserStatus(token);

      if (isSuccess && result) {
        sessionStorage.removeItem("age");
        sessionStorage.removeItem("goal");

        if (getUserAgent() === "apple")
          window.webkit?.messageHandlers?.completeSignup.postMessage("");
        else if (getUserAgent() === "android") {
          window.Android.completeSignUp();
        } else {
          const { isSuccess, result, message } = await getTopic();
          if (isSuccess) {
            const { isSuccess, message } = await postNotification({
              pushTopicId: result[0].id,
              scheduleTime: "1200",
            });
            if (!isSuccess) openModal({ type: "guide", message: message });
          } else openModal({ type: "guide", message: message });
        }

        // navigate("/guide");
        navigate("/");
      } else openModal({ type: "guide", message: message });
    } catch (e: any) {
      console.log(e);
      // TODO(i18n-phase4): BE 가 errorCode 로 응답하면 그걸 비교. 지금은 message 문자열 비교 유지.
      if (isTokenRefreshFailed(e))
        openModal({
          type: "login",
          message: t("auth:login.autoLogoutWarning"),
        });
      else openModal({ type: "guide", message: t("errors:generic.shortTitle") });
    } finally {
      window.localStorage.removeItem("INACTIVE");
    }
  };

  // tailWidth 는 더 이상 layout 에 사용 안 함 (grid 가 자동 균등). 호환 위해 ref 만 유지.
  void tailWidth;

  return (
    <HeaderStyled
      grey={pathname.includes("study") || pathname === "/mypage/edit"}
    >
      <div className="lead">
        {back && (
          <img
            className="back"
            src={iconBack}
            alt=""
            onClick={backFunc ? backFunc : () => navigate(-1)}
          />
        )}
      </div>
      {text && <span className="title">{text}</span>}
      <div className="tail">
        {skip && (
          <span className="skip" onClick={skipFunc}>
            {t("common:action.skip")}
          </span>
        )}
        {right && (
          <span className="right" onClick={rightFunc}>
            {right}
          </span>
        )}
      </div>
    </HeaderStyled>
  );
};

export default AppHeader;

const HeaderStyled = styled.header<{
  grey: boolean;
}>`
  width: 100vw;
  /* 모바일 모방 max-width: 42rem (grey 페이지는 100vw 그대로 wide). margin: 0 auto
     로 wide screen 에서 화면 중앙 정렬 → 본문(같은 max-width 박스) 와 좌우 끝이
     일치 → < 가 본문 좌측, 삭제하기가 본문 우측 마진과 정렬됨. */
  max-width: ${({ grey }) => (grey ? "100vw" : "42rem")};
  margin: 0 auto;
  height: 4.6rem;
  padding: 0 2rem;
  /* grid 3열 — 좌(lead) / 중앙(title) / 우(tail). 좌·우 trace 영역이 1fr 동일 분배 →
     title 은 항상 정확히 중앙, right/skip 은 항상 우측 정렬. (이전 flex + JS width
     측정 trick 의 first-render race condition 으로 정렬 깨지던 회귀 fix.) */
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;

  background-color: ${({ grey }) =>
    grey ? "var(--color-grey)" : "var(--color-white)"};

  .lead {
    justify-self: start;
    display: flex;
    align-items: center;

    .back {
      width: 2.4rem;
      height: 2.4rem;
      cursor: pointer;
    }
  }

  .title {
    justify-self: center;
    font-size: 1.6rem;
    font-weight: 600;
    color: var(--color-grey-900);
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 0.4rem;
    max-width: 24rem;
  }

  .tail {
    justify-self: end;
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  .skip {
    font-size: 1.2rem;
    color: var(--color-grey-500);
    white-space: nowrap;
    cursor: pointer;
  }

  .right {
    font-size: 1.4rem;
    color: var(--color-grey-500);
    padding: 0 0.2rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    & > .select-all {
      font-size: 1.2rem;
    }
  }
`;
